/**
 * La geometría del visor de panorámicas. Sin dependencias, a propósito.
 *
 * <p>Vive aparte del componente porque es la parte que se puede equivocar en
 * silencio, y así la prueba del portal la cubre —el panel no tiene corredor de
 * pruebas, pero el fichero es el mismo—. Además: es la parte que se puede
 * equivocar en silencio. Si `aPantalla` y `desdePantalla` no son inversas
 * exactas, los puntos de paso quedan donde no se pulsó y nada falla —solo sale
 * mal, unos grados corrido, y cuesta mucho darse cuenta—.
 *
 * <p><b>Hay una copia de este fichero en el panel</b>
 * (`apps/portal/src/api/geometriaPanorama.ts`). El panel y el portal son paquetes
 * de npm independientes, sin espacio de trabajo compartido, y montarlo para
 * compartir dos funciones sería cambiar el sistema de compilación de los dos.
 * Los dos ficheros son idénticos; si se toca uno, hay que tocar el otro.
 *
 * <p>Las cuentas son las mismas que hace el shader, en el mismo orden de giros.
 * El shader va de píxel a color; esto va de píxel a ángulo y de ángulo a píxel.
 */

/*
 * La relación entre el yaw y la columna que se muestrea, medida contra el
 * shader en un navegador real:
 *
 *   yaw -90° → u ≈ 0.75      yaw 0° → u ≈ 0.50      yaw +90° → u ≈ 0.25
 *
 * O sea: **la u baja cuando el yaw sube**. De ahí que el arrastre horizontal
 * sume al yaw para mirar a la izquierda. Queda anotado porque el signo parece
 * al revés de lo natural y es justo el que se equivoca.
 *
 * En vertical no hay sorpresa: subir el pitch mira arriba (pitch 60° → v ≈ 0.17,
 * y v = 0 es el borde de arriba de la equirectangular).
 */

export interface Camara {
  /** En radianes. */
  yaw: number;
  /** En radianes. */
  pitch: number;
  /** Campo de visión vertical, en radianes. */
  fov: number;
}

export interface PuntoEsferico {
  /** En grados. */
  yaw: number;
  /** En grados. */
  pitch: number;
}

/** Hacia dónde mira un píxel de la pantalla, en grados. */
export function desdePantalla(
  px: number,
  py: number,
  ancho: number,
  alto: number,
  camara: Camara,
): PuntoEsferico {
  const x = (px / ancho) * 2 - 1;
  const y = 1 - (py / alto) * 2;

  const tanMitad = Math.tan(camara.fov / 2);
  const aspecto = ancho / alto;
  let d = [x * tanMitad * aspecto, y * tanMitad, -1];
  const norma = Math.hypot(d[0]!, d[1]!, d[2]!);
  d = [d[0]! / norma, d[1]! / norma, d[2]! / norma];

  const cp = Math.cos(camara.pitch);
  const sp = Math.sin(camara.pitch);
  d = [d[0]!, d[1]! * cp - d[2]! * sp, d[1]! * sp + d[2]! * cp];
  const cy = Math.cos(camara.yaw);
  const sy = Math.sin(camara.yaw);
  d = [d[0]! * cy + d[2]! * sy, d[1]!, -d[0]! * sy + d[2]! * cy];

  return {
    yaw: (Math.atan2(d[0]!, -d[2]!) * 180) / Math.PI,
    pitch: (Math.asin(Math.max(-1, Math.min(1, d[1]!))) * 180) / Math.PI,
  };
}

/**
 * Dónde cae en la pantalla un punto de la esfera.
 *
 * <p>Nulo si queda detrás de la cámara o muy fuera del encuadre: un punto de
 * paso a la espalda no se dibuja.
 */
export function aPantalla(
  punto: PuntoEsferico,
  ancho: number,
  alto: number,
  camara: Camara,
): { x: number; y: number } | null {
  const lon = (punto.yaw * Math.PI) / 180;
  const lat = (punto.pitch * Math.PI) / 180;

  // Los mismos giros, en orden inverso y con el signo cambiado.
  let d = [Math.cos(lat) * Math.sin(lon), Math.sin(lat), -Math.cos(lat) * Math.cos(lon)];
  const cy = Math.cos(-camara.yaw);
  const sy = Math.sin(-camara.yaw);
  d = [d[0]! * cy + d[2]! * sy, d[1]!, -d[0]! * sy + d[2]! * cy];
  const cp = Math.cos(-camara.pitch);
  const sp = Math.sin(-camara.pitch);
  d = [d[0]!, d[1]! * cp - d[2]! * sp, d[1]! * sp + d[2]! * cp];

  if (d[2]! >= -0.001) return null;

  const tanMitad = Math.tan(camara.fov / 2);
  const aspecto = ancho / alto;
  const x = (d[0]! / -d[2]!) / (tanMitad * aspecto);
  const y = (d[1]! / -d[2]!) / tanMitad;
  if (Math.abs(x) > 1.2 || Math.abs(y) > 1.2) return null;

  return { x: ((x + 1) / 2) * ancho, y: ((1 - y) / 2) * alto };
}
